// src/pages/ProfilePage.js
import React, { useEffect, useState } from "react";
import { ROLES, resolvePersistedUserPhoto } from "../utils/roles";
import { useAuth } from "../context/AuthContext";
import Card from "../ui/Card";
import { storage } from "../firebase";
import { getDownloadURL, ref, uploadBytes } from "firebase/storage";
import { getOrgLogoAsset } from "../utils/brandAssets";
import {
  COUNTRY_OPTIONS,
  LOCATION_CITY_OTHER,
  SOUTH_AFRICA_CITIES,
  SOUTH_AFRICA_PROVINCES,
  isSouthAfricaCountry,
  normalizeLocationCountry,
} from "../utils/locationOptions";

export default function ProfilePage() {
  const { user, activeOrgId, updateRole, updateProfile } = useAuth();
  const [firstName, setFirstName] = useState("");
  const [lastName,  setLastName]  = useState("");
  const [username,  setUsername]  = useState("");
  const [email,     setEmail]     = useState("");
  const [country,   setCountry]   = useState("");
  const [province,  setProvince]  = useState("");
  const [city,      setCity]      = useState("");
  const [customCity, setCustomCity] = useState("");
  const [draftPhotoUrl, setDraftPhotoUrl] = useState("");
  const [photoFile, setPhotoFile] = useState(null);
  const [role, setRole] = useState(user?.role || "member");

  useEffect(() => {
    setFirstName(user?.firstName || "");
    setLastName(user?.lastName || "");
    setUsername(user?.username || "");
    setEmail(user?.email || "");
    const nextCountry = normalizeLocationCountry(user?.country || user?.countryCode || "");
    const nextCity = String(user?.city || user?.town || user?.locality || "").trim();
    setCountry(nextCountry);
    setProvince(user?.province || user?.state || user?.region || "");
    setCity(nextCity);
    setCustomCity(SOUTH_AFRICA_CITIES.includes(nextCity) ? "" : nextCity);
    setRole(user?.role || "member");
  }, [user]);

  const fallbackPhotoUrl = getOrgLogoAsset({
    orgId: activeOrgId || user?.orgId || "org_default",
    mode: "light",
    compact: true,
  });
  const persistedPhotoUrl = resolvePersistedUserPhoto(user);
  const previewPhotoUrl = draftPhotoUrl || persistedPhotoUrl || fallbackPhotoUrl;

  const onPhoto = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (!String(f.type || "").startsWith("image/")) {
      alert("Please select an image file.");
      return;
    }
    if (f.size > 1 * 1024 * 1024) {
      alert("Profile image must be 1MB or smaller.");
      return;
    }
    setPhotoFile(f);
    const r = new FileReader();
    r.onload = () => setDraftPhotoUrl(r.result?.toString() || "");
    r.readAsDataURL(f);
  };

  const onSave = async () => {
    let nextPhotoUrl = user?.photoURL || "";
    const southAfricaSelected = isSouthAfricaCountry(country);
    const normalizedCountry = normalizeLocationCountry(country);
    const normalizedProvince = southAfricaSelected && SOUTH_AFRICA_PROVINCES.includes(province) ? province : "";
    const normalizedCity = southAfricaSelected
      ? (city === LOCATION_CITY_OTHER ? customCity.trim() : city.trim())
      : "";
    if (user?.uid && photoFile) {
      try {
        const safeName = (photoFile.name || "profile-image")
          .toLowerCase()
          .replace(/[^a-z0-9._-]+/g, "-");
        const path = `users/${user.uid}/profile/${Date.now()}-${safeName}`;
        const storageRef = ref(storage, path);
        await uploadBytes(storageRef, photoFile, { contentType: photoFile.type || "image/jpeg" });
        nextPhotoUrl = await getDownloadURL(storageRef);
      } catch {
        alert("Profile image upload failed. Please try again.");
        return;
      }
    }
    if (typeof updateProfile === "function") {
      const ok = await updateProfile({
        firstName,
        lastName,
        username,
        email,
        country: normalizedCountry,
        province: normalizedProvince,
        city: normalizedCity,
        photoURL: nextPhotoUrl,
      });
      if (!ok) {
        alert("Profile save failed. Please try again.");
        return;
      }
    }
    setDraftPhotoUrl("");
    setPhotoFile(null);
    alert("Profile saved.");
  };

  const onRoleChange = (e) => {
    const next = e.target.value;
    setRole(next);
    // keep AuthContext in sync so sidebar/routes re-render immediately
    updateRole(next);
  };

  const isSuperadmin = user?.role === "superadmin";
  const isReadOnly = user?.role === "investor";
  const southAfricaSelected = isSouthAfricaCountry(country);
  const countryOptionValues = COUNTRY_OPTIONS.map((option) => option.value);
  const showExistingCountryOption = country && !countryOptionValues.includes(country);
  const citySelection = SOUTH_AFRICA_CITIES.includes(city)
    ? city
    : city || customCity
      ? LOCATION_CITY_OTHER
      : "";

  return (
    <div className="nia-page" style={{ maxWidth: 720, margin: "0 auto" }}>
      <Card className="nia-hero">
        <div className="nia-header">
          <div>
            <h2>Profile & Identity</h2>
            <p>Keep your coaching profile consistent across Nia COACH experiences.</p>
          </div>
        </div>
      </Card>

      <Card title="Profile Details">
        <div className="nia-form">
          <label>Upload Profile Picture</label>
          <input type="file" accept="image/*" onChange={onPhoto} disabled={isReadOnly} />
          <div style={{ margin: "12px 0" }}>
            <img
              src={previewPhotoUrl}
              alt="Profile"
              width={72}
              height={72}
              style={{ borderRadius: "50%", objectFit: "cover" }}
              onError={(e) => { e.currentTarget.src = fallbackPhotoUrl; }}
            />
          </div>

          <label>First Name</label>
          <input value={firstName} onChange={e => setFirstName(e.target.value)} disabled={isReadOnly} />

          <label>Last Name</label>
          <input value={lastName} onChange={e => setLastName(e.target.value)} disabled={isReadOnly} />

          <label>Username</label>
          <input value={username} onChange={e => setUsername(e.target.value)} disabled={isReadOnly} />

          <label>Email</label>
          <input value={email} onChange={e => setEmail(e.target.value)} disabled={isReadOnly} />

          <label>Country</label>
          <select
            value={country}
            onChange={(e) => {
              const nextCountry = normalizeLocationCountry(e.target.value);
              setCountry(nextCountry);
              if (!isSouthAfricaCountry(nextCountry)) {
                setProvince("");
                setCity("");
                setCustomCity("");
              }
            }}
            disabled={isReadOnly}
          >
            {showExistingCountryOption ? <option value={country}>{country} (not configured)</option> : null}
            {COUNTRY_OPTIONS.map((option) => (
              <option key={option.label} value={option.value}>{option.label}</option>
            ))}
          </select>

          <label>Province / State</label>
          <select
            value={southAfricaSelected ? province : ""}
            onChange={(e) => setProvince(e.target.value)}
            disabled={isReadOnly || !southAfricaSelected}
          >
            <option value="">{southAfricaSelected ? "Select province" : "Select South Africa first"}</option>
            {SOUTH_AFRICA_PROVINCES.map((option) => (
              <option key={option} value={option}>{option}</option>
            ))}
          </select>

          <label>City</label>
          <select
            value={southAfricaSelected ? citySelection : ""}
            onChange={(e) => {
              const nextCity = e.target.value;
              setCity(nextCity);
              if (nextCity !== LOCATION_CITY_OTHER) setCustomCity("");
            }}
            disabled={isReadOnly || !southAfricaSelected}
          >
            <option value="">{southAfricaSelected ? "Select city" : "Select South Africa first"}</option>
            {SOUTH_AFRICA_CITIES.map((option) => (
              <option key={option} value={option}>{option}</option>
            ))}
            <option value={LOCATION_CITY_OTHER}>Other / not listed</option>
          </select>

          {southAfricaSelected && citySelection === LOCATION_CITY_OTHER ? (
            <>
              <label>City not listed</label>
              <input value={customCity} onChange={e => setCustomCity(e.target.value)} disabled={isReadOnly} />
            </>
          ) : null}

          <div className="nia-muted">Used only to improve location-aware support. Nia should not guess your location.</div>

          <button className="btn btn-primary" onClick={onSave} disabled={isReadOnly}>Save Changes</button>
        </div>
      </Card>

      {isSuperadmin && (
        <Card title="Role & Access">
          <div className="nia-form">
            <label>Role (admin)</label>
            <select value={role} onChange={onRoleChange} disabled={isReadOnly}>
              {ROLES.map(r => <option key={r} value={r}>{r}</option>)}
            </select>
            <div className="nia-muted">
              Updates navigation & permissions immediately.
            </div>
          </div>
        </Card>
      )}
    </div>
  );
}
